import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Check, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_CFG = {
  pendente: { label: 'Pendente', color: 'bg-yellow-500/20 text-yellow-400' },
  recebido: { label: 'Recebido', color: 'bg-green-500/20 text-green-400' },
  vencido: { label: 'Vencido', color: 'bg-red-500/20 text-red-400' },
};

export default function Receivables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/receivables/');
      setItems(res.data);
    } catch { toast.error('Erro ao carregar contas'); }
    finally { setLoading(false); }
  };

  const markReceived = async (id) => {
    try {
      await api.put(`/receivables/${id}`, { status: 'recebido' });
      toast.success('Marcado como recebido');
      load();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const total = items.reduce((s, r) => s + r.valor, 0);
  const received = items.filter(r => r.status === 'recebido').reduce((s, r) => s + r.valor, 0);
  const pending = items.filter(r => r.status === 'pendente').reduce((s, r) => s + r.valor, 0);
  const overdue = items.filter(r => r.status === 'vencido').reduce((s, r) => s + r.valor, 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <h1 className="font-heading text-2xl font-bold text-white">Contas a Receber</h1>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: total, color: 'text-white' },
          { label: 'Recebido', value: received, color: 'text-green-400' },
          { label: 'Pendente', value: pending, color: 'text-yellow-400' },
          { label: 'Vencido', value: overdue, color: 'text-red-400' },
        ].map(k => (
          <div key={k.label} className="stat-card">
            <div className="text-xs text-zinc-500 mb-1">{k.label}</div>
            <div className={`font-mono font-bold text-lg ${k.color}`}>{brl(k.value)}</div>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800">
            <tr className="text-xs text-zinc-500">
              <th className="text-left px-4 py-3">Orç.</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-right px-4 py-3">Valor</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Vencimento</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><div className="inline-block w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : items.map(r => {
              const cfg = STATUS_CFG[r.status] || STATUS_CFG.pendente;
              return (
                <tr key={r.id} data-testid={`receivable-row-${r.id}`} className="table-row-hover">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">#{r.num}</td>
                  <td className="px-4 py-3 text-white font-medium">{r.cliente}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{brl(r.valor)}</td>
                  <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{r.vencimento}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${cfg.color}`}>{cfg.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {r.status !== 'recebido' && (
                      <button onClick={() => markReceived(r.id)}
                        className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 border border-green-500/30 hover:bg-green-500/10 px-2 py-1 rounded-lg transition-colors">
                        <Check size={12} />
                        Recebido
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm">Nenhuma conta a receber</div>
        )}
      </div>
    </div>
  );
}
